/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";
import { columns as portColumns } from "./PortData";
import { getPorts, Port, getDataCentersIdsNames, getHardwareByDatacenterId, getAllHardware, getPortId, DataCenterItem, Hardware, updatePort, UpdatePortPayload, createPort, CreatePortPayload } from "../request/request";
import { useAppSelector } from "@redux/hooks";
import { ParsedFilter as OldParsed, FilterField, buildPredicate, labelId, parseToken } from "../../../../helpers/advancedFilters";
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusNV.svg";
import { ReactComponent as IconEye } from "../../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { PortViewModal } from "./Modals/PortViewModal";
import { PortCreateModal } from "./Modals/PortCreateModal";
import { PortEditModal } from "./Modals/PortEditModal";
import { deletePort } from "../request/request";

// Constant for filtered limit
const FILTERED_LIMIT = 1000;

export default function PortInventoryPage() {
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();
  const [allPorts, setAllPorts] = useState<Port[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  
  // Dropdown data sources
  const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
  const [allHardware, setAllHardware] = useState<Hardware[]>([]);
  const [isLoadingHardware, setIsLoadingHardware] = useState(false);
  
  // Filter selections - using IDs instead of names
  const [selectedDatacenterId, setSelectedDatacenterId] = useState<string | undefined>(undefined);
  const [selectedHardwareResourceId, setSelectedHardwareResourceId] = useState<number | undefined>(undefined);
  // Track if "All" is selected (special case)
  const [isAllDatacentersSelected, setIsAllDatacentersSelected] = useState(false);
  
  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // Cache for ports
  const [portsCache, setPortsCache] = useState<Map<string, Port[]>>(new Map());

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedPort, setSelectedPort] = useState<Port | null>(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);

  // Delete confirmation
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [portToDelete, setPortToDelete] = useState<Port | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Rows per page state for UI table pagination (default 20)
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Helper function to calculate query key
  // Note: filtersKey is NOT included because searchbar filters are applied client-side
  const getQueryKey = (
    limit: number | undefined,
    datacenterId: string | undefined,
    hardwareResourceId: number | undefined
  ): string => {
    return JSON.stringify({ limit, datacenterId, hardwareResourceId });
  };

  // Helper function to extract data from API response
  const extractApiData = (resp: any): any => {
    return (resp as any)?.data?.data ?? (resp as any)?.data;
  };

  // Helper function to map raw port data to Port interface (detailed version with all fields)
  const mapRawDataToPort = (rawData: any): Port => {
    return {
      id: String(rawData.id),
      portName: rawData.portDescription || rawData.portName || "",
      portNumber: rawData.portNumber || "",
      portType: rawData.portType || "",
      portStatus: rawData.portStatus || "",
      isAvailable: rawData.isAvailable ?? false,
      hardwareHostname: rawData.hardwareHostname || "",
      datacenterDisplayName: rawData.datacenterDisplayName || "",
      datacenterId: rawData.datacenterId || "",
      hardwareResourceId: rawData.hardwareResourceId || undefined,
      KEPorts: rawData.KEPorts || [],
      isNew: false,
      isEditing: false,
      // Additional fields from detailed response
      clientAssignation: rawData.clientAssignation || "",
      decommissioned: rawData.decommissioned ?? false,
      isShared: rawData.isShared ?? false,
      createdAt: rawData.createdAt || "",
      updatedAt: rawData.updatedAt || "",
      kePortCount: rawData.kePortCount || 0,
    };
  };

  // Helper function to invalidate cache and refetch ports
  const refetchPorts = async () => {
    setPortsCache(new Map());
    const params = getFetchParams();
    const abortController = new AbortController();
    await fetchPorts(params.limit, params.datacenterId, params.hardwareResourceId, abortController.signal);
  };

  // Fetch datacenters on mount only
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    const fetchDatacenters = async () => {
      try {
        const token = await getAccessTokenSilently();
        
        // Fetch datacenters only
        const dcResp = await getDataCentersIdsNames(token);
        const dcRaw = extractApiData(dcResp) ?? [];
        const shapedDataCenters: DataCenterItem[] = dcRaw.map((dc: any) => ({
          datacenterId: dc.datacenterId,
          name: dc.name,
          displayName: dc.displayName ?? dc.name,
        }));
        setDataCenters(shapedDataCenters);
      } catch (err) {
        console.error("Error fetching datacenters:", err);
      }
    };
    
    fetchDatacenters();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // Fetch hardware when datacenter is selected or "All" is selected
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    // If "Select an option" is selected, clear hardware list
    if (!selectedDatacenterId && !isAllDatacentersSelected) {
      setAllHardware([]);
      setIsLoadingHardware(false);
      return;
    }

    const fetchHardware = async () => {
      try {
        setIsLoadingHardware(true);
        const token = await getAccessTokenSilently();
        
        if (isAllDatacentersSelected) {
          // Fetch all hardware when "All" is selected
          const hwResp = await getAllHardware(token);
          const hwRaw = extractApiData(hwResp) ?? [];
          setAllHardware(hwRaw);
        } else if (selectedDatacenterId) {
          // Fetch hardware for selected datacenter
          const hwResp = await getHardwareByDatacenterId(token, selectedDatacenterId);
          const hwRaw = extractApiData(hwResp) ?? [];
          setAllHardware(hwRaw);
        }
      } catch (err) {
        console.error("Error fetching hardware:", err);
        setAllHardware([]);
      } finally {
        setIsLoadingHardware(false);
      }
    };
    
    fetchHardware();
  }, [selectedDatacenterId, isAllDatacentersSelected, isLoading, isAuthenticated, getAccessTokenSilently]);

  // Centralized function to decide fetch parameters
  // Note: limit is for backend, separate from rowsPerPage (UI pagination)
  const getFetchParams = () => {
    // Determine backend limit based on selection (independent of UI rowsPerPage):
    // - "All" datacenter: 100
    // - Mount (no selection): 100
    // - Specific datacenter: 1000
    // - Hardware selected: 1000
    let limit = 100; // Default: 100 for mount and "All" datacenter
    
    if (isAllDatacentersSelected) {
      // "All" datacenter: use 100
      limit = 100;
    } else if (selectedDatacenterId) {
      // Specific datacenter selected: use 1000
      limit = FILTERED_LIMIT;
    }
    
    // If hardware is selected, use 1000
    if (selectedHardwareResourceId !== undefined) {
      limit = FILTERED_LIMIT;
    }
    
    return {
      limit,
      // Only pass datacenterId if a specific datacenter is selected (not "All")
      datacenterId: isAllDatacentersSelected ? undefined : selectedDatacenterId,
      hardwareResourceId: selectedHardwareResourceId
    };
  };

  // Load ports with AbortController support
  const fetchPorts = async (
    limit: number | undefined,
    datacenterId: string | undefined,
    hardwareResourceId: number | undefined,
    signal: AbortSignal
  ) => {
    try {
      setIsFetching(true);
      const token = await getAccessTokenSilently();
      const resp = await getPorts(token, {
        limit,
        datacenterId,
        hardwareResourceId,
        signal
      });
      const rawData = extractApiData(resp);
      const portsArray = rawData?.ports || [];
      
      const mappedPorts: Port[] = portsArray.map((p: any) => ({
        id: String(p.id),
        portName: p.portDescription || p.portName || "",
        portNumber: p.portNumber || "",
        portType: p.portType || "",
        portStatus: p.portStatus || "",
        isAvailable: p.isAvailable ?? false,
        hardwareHostname: p.hardwareHostname || "",
        datacenterDisplayName: p.datacenterDisplayName || "",
        datacenterId: p.datacenterId || "",
        hardwareResourceId: p.hardwareResourceId || undefined,
        KEPorts: p.KEPorts || [],
        isNew: false,
        isEditing: false,
        clientAssignation: p.clientAssignation || "",
      }));
      
      setAllPorts(mappedPorts);
      return mappedPorts;
    } catch (err: any) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        // Ignore abort errors
        return;
      }
      console.error("Error fetching ports:", err);
      setAllPorts([]);
      throw err;
    } finally {
      setIsFetching(false);
    }
  };

  // Main useEffect for fetching ports
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    const abortController = new AbortController();
    const params = getFetchParams();
    const queryKey = getQueryKey(params.limit, params.datacenterId, params.hardwareResourceId);
    
    // Check cache first
    const cached = portsCache.get(queryKey);
    if (cached) {
      setAllPorts(cached);
      return;
    }
    
    // Fetch with abort signal
    fetchPorts(params.limit, params.datacenterId, params.hardwareResourceId, abortController.signal)
      .then((ports) => {
        if (ports) {
          // Save to cache
          setPortsCache(prev => new Map(prev).set(queryKey, ports));
        }
      })
      .catch(() => {
        // Error already handled in fetchPorts
      });
    
    return () => {
      abortController.abort();
    };
  }, [selectedDatacenterId, selectedHardwareResourceId, isAuthenticated, getAccessTokenSilently]);

  // Datacenter options from fetched data
  const dataCenterOptions = useMemo(() => {
    const labels = dataCenters.map(dc => dc.displayName || dc.name).sort();
    return ["Select an option", "All", ...labels];
  }, [dataCenters]);

  // Hardware options - only show hardware for selected datacenter
  const hardwareOptions = useMemo(() => {
    const hostnames = allHardware.map(hw => hw.hostname).filter(Boolean).sort();
    return ["All", ...hostnames];
  }, [allHardware]);

  // Check if hardware dropdown should be enabled
  const isHardwareEnabled = useMemo(() => {
    return selectedDatacenterId !== undefined || isAllDatacentersSelected;
  }, [selectedDatacenterId, isAllDatacentersSelected]);

  // Maps for converting between labels and IDs
  const datacenterNameToIdMap = useMemo(() => {
    const map = new Map<string, string>();
    dataCenters.forEach(dc => {
      const label = dc.displayName || dc.name;
      map.set(label, dc.datacenterId);
    });
    return map;
  }, [dataCenters]);

  const hardwareHostnameToIdMap = useMemo(() => {
    const map = new Map<string, number>();
    allHardware.forEach(hw => {
      if (hw.hostname) {
        map.set(hw.hostname, hw.id);
      }
    });
    return map;
  }, [allHardware]);

  // Get selected datacenter label
  const selectedDatacenterLabel = useMemo(() => {
    if (isAllDatacentersSelected) return "All";
    if (!selectedDatacenterId) return "Select an option";
    const dc = dataCenters.find(dc => dc.datacenterId === selectedDatacenterId);
    return dc?.displayName || dc?.name || "Select an option";
  }, [selectedDatacenterId, isAllDatacentersSelected, dataCenters]);

  // Get selected hardware label
  const selectedHardwareLabel = useMemo(() => {
    if (!selectedHardwareResourceId) return "All";
    const hw = allHardware.find(hw => hw.id === selectedHardwareResourceId);
    return hw?.hostname || "All";
  }, [selectedHardwareResourceId, allHardware]);

  // Filter fields for GenericSearchbar
  const portFields: FilterField<Port>[] = useMemo(() => [
    { label: "Port Number", accessor: (row) => row.portNumber || "", type: "string", inFreeText: true },
    { label: "Type", accessor: (row) => row.portType || "", type: "string", inFreeText: true },
    { label: "Port Name", accessor: (row) => row.portName || "", type: "string", inFreeText: true },
    { label: "Status", accessor: (row) => row.portStatus || "", type: "string", inFreeText: true },
    { 
      label: "Availability", 
      accessor: (row) => {
        // Return the raw clientAssignation value, or "unassigned" for null/empty
        const clientAssignation = row.clientAssignation?.toLowerCase() || "";
        return clientAssignation || "unassigned";
      },
      type: "string",
      inFreeText: true
    },
    { 
      label: "KE Ports", 
      accessor: (row) => {
        const kePorts = row.KEPorts || [];
        return kePorts.map((ke) => ke.subscriptionId).join(" ");
      }, 
      type: "string" 
    },
  ], []);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of portFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [portFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Port>>();
    for (const f of portFields) {
      const id = f.key ?? labelId(f.label);
      m.set(id.toLowerCase(), f);
    }
    return m;
  }, [portFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":"; // contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Port>(normalizedFilters, portFields),
    [normalizedFilters, portFields]
  );

  // Filter ports based on search filters (dropdowns are handled server-side)
  const filteredPorts = useMemo(() => {
    let filtered = allPorts;
    
    // Apply search filters if any (client-side filtering)
    if (predicate) {
      filtered = filtered.filter(predicate);
    }
    
    return filtered;
  }, [allPorts, predicate]);

  // Available hardware options (already filtered by datacenter in hardwareOptions)
  // Note: availableHardwareOptions is the same as hardwareOptions, so we use hardwareOptions directly

  // Handler: Normalize filters before setting
  const handleFiltersChange = useCallback((filters: OldParsed[]) => {
    const normalized = filters
      .map(f => ({
        ...f,
        key: f.key?.trim().toLowerCase(),
        value: f.value?.trim()
      }))
      .filter(f => f.value && f.value.length > 0);
    setActiveFilters(normalized);
  }, []);

  const handleDataCenterSelect = (selected: string) => {
    if (selected === "Select an option") {
      // Reset both dropdowns and clear hardware
      setSelectedDatacenterId(undefined);
      setIsAllDatacentersSelected(false);
      setSelectedHardwareResourceId(undefined);
      setAllHardware([]);
    } else if (selected === "All") {
      // For "All", enable hardware dropdown and fetch all hardware
      setSelectedDatacenterId(undefined);
      setIsAllDatacentersSelected(true);
      setSelectedHardwareResourceId(undefined);
    } else {
      // Find datacenter ID from display name
      const datacenterId = datacenterNameToIdMap.get(selected);
      if (datacenterId) {
        setSelectedDatacenterId(datacenterId);
        setIsAllDatacentersSelected(false);
      } else {
        console.warn("Datacenter not found for label:", selected);
      }
      // Reset hardware selection when datacenter changes
      setSelectedHardwareResourceId(undefined);
    }
  };

  const handleHardwareSelect = (selected: string) => {
    if (selected === "All") {
      // Reset only hardware filter, keep datacenter filter
      setSelectedHardwareResourceId(undefined);
    } else {
      // Find hardware ID from hostname
      const hardwareResourceId = hardwareHostnameToIdMap.get(selected);
      if (hardwareResourceId !== undefined) {
        setSelectedHardwareResourceId(hardwareResourceId);
      } else {
        console.warn("Hardware not found for hostname:", selected);
      }
    }
  };

  // Handler: View Port
  const handleViewPort = async (port: Port) => {
    setIsViewModalOpen(true);
    setIsViewLoading(true);
    setSelectedPort(null);
    
    try {
      const token = await getAccessTokenSilently();
      const resp = await getPortId(token, port.id);
      const rawData = extractApiData(resp);
      
      // Map the detailed port data to Port interface
      const detailedPort = mapRawDataToPort(rawData);
      
      setSelectedPort(detailedPort);
    } catch (error) {
      console.error("Error loading port for view:", error);
      // Fallback to using the port data we already have
      setSelectedPort(port);
    } finally {
      setIsViewLoading(false);
    }
  };

  // Handler: Edit Port
  const handleEditPort = async (port: Port) => {
    setIsEditModalOpen(true);
    setIsEditLoading(true);
    setSelectedPort(null);
    
    try {
      // Fetch detailed port data for edit
      const token = await getAccessTokenSilently();
      const resp = await getPortId(token, port.id);
      const rawData = extractApiData(resp);
      
      // Map the detailed port data to Port interface
      const detailedPort = mapRawDataToPort(rawData);
      
      setSelectedPort(detailedPort);
    } catch (error) {
      console.error("Error loading port for edit:", error);
      // Fallback to using the port data we already have
      setSelectedPort(port);
    } finally {
      setIsEditLoading(false);
    }
  };

  // Handler: Create Port
  const handleCreatePort = () => {
    setIsCreateModalOpen(true);
    setSelectedPort(null);
  };

  // Handler: Delete Port
  const handleDeletePort = (port: Port) => {
    setPortToDelete(port);
    setIsAlertOpen(true);
  };

  // Handler: Confirm Delete
  const handleConfirmDelete = async () => {
    if (!portToDelete) return;
    
    try {
      setIsDeleting(true);
      const token = await getAccessTokenSilently();
      await deletePort(token, portToDelete.id);
      
      // Invalidate cache and refetch
      await refetchPorts();
      
      // Close alert and reset state
      setIsAlertOpen(false);
      setPortToDelete(null);
    } catch (error) {
      console.error("Error deleting port:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handler: Save Port (for create and edit)
  const handleSavePort = async (portData: Partial<Port>) => {
    try {
      const token = await getAccessTokenSilently();
      
      if (selectedPort && isEditModalOpen) {
        // Update existing port
        // Map portData to UpdatePortPayload format
        const payload: UpdatePortPayload = {
          portDescription: (portData as any).portDescription || portData.portName || "",
          portId: selectedPort.id,
          portNumber: portData.portNumber || "",
          portType: portData.portType || "",
          portStatus: portData.portStatus || "available",
          clientAssignation: portData.clientAssignation || "",
          isAvailable: (portData as any).isAvailable ?? false,
          decommissioned: (portData as any).decommissioned ?? false,
          isShared: (portData as any).isShared ?? false,
        };
        
        await updatePort(token, selectedPort.id, payload);
        
        // Invalidate cache and refetch
        await refetchPorts();
        
        setIsEditModalOpen(false);
        setSelectedPort(null);
      } else {
        // Create new port
        // Map portData to CreatePortPayload format
        const payload: CreatePortPayload = {
          hardwareResourceId: (portData as any).hardwareResourceId ? Number((portData as any).hardwareResourceId) : 0,
          portDescription: (portData as any).portDescription || portData.portName || "",
          portNumber: portData.portNumber || "",
          portType: portData.portType || "",
          portStatus: portData.portStatus || "available",
          clientAssignation: portData.clientAssignation || "",
          isAvailable: (portData as any).isAvailable ?? false,
          decommissioned: (portData as any).decommissioned ?? false,
        };
        
        await createPort(token, payload);
        
        // Invalidate cache and refetch
        await refetchPorts();
        
        setIsCreateModalOpen(false);
        setSelectedPort(null);
      }
    } catch (error) {
      console.error("Error saving port:", error);
      throw error;
    }
  };

  // Handler: Rows per page change (UI only, does not trigger backend refetch)
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    // Note: This only changes UI pagination, backend data is already loaded
  };

  // Determine if datacenter column should be shown
  const showDatacenterColumn = !selectedDatacenterId;

  // Override Actions column and conditionally filter datacenter column
  const columns = portColumns
    .filter((col) => {
      // Hide datacenter column when a specific datacenter is selected
      if (col.header === "Datacenter" || col.accessor === "datacenterDisplayName") {
        return showDatacenterColumn;
      }
      return true;
    })
    .map((col) => {
      if (col.header === "Actions" && col.renderCell) {
        return {
          ...col,
          renderCell: (row: Port) => (
            <div className="flex items-center gap-2">
              {/* View */}
              <GenericButton
              variant="secondary"
              onClick={() => handleViewPort(row)}
              icon={<IconEye className="w-5 h-5 text-semantic-text-secondary" />}
              />
          
              {/* Edit */}
              <GenericButton
              variant="secondary"
              onClick={() => handleEditPort(row)}
              icon={<IconEdit className="w-6 h-6 text-semantic-text-secondary" />}
              />
          
              {/* Delete */}
              <GenericButton
                variant="danger"
                onClick={() => handleDeletePort(row)}
                icon={<IconDelete className="w-6 h-6" />}
              />

            </div>
          ),
        };
      }
      return col;
    });

  const suggestedKeys = useMemo(() => {
    const headers = portFields.map((f) => f.label);

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [portFields, isViewingAllCustomers]);

  return (
    <>
      <div className="flex-col space-y-5">
        {/* Select DataCenter and Hardware */}
        <GenericCard isCollapsible={false}>
          <div className="flex items-center justify-between p-5">
            <div className="flex items-center gap-4">
              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                  Select a Data Center
                </label>
                <GenericDropdown
                  items={dataCenterOptions}
                  defaultLabel="Select a data center"
                  value={selectedDatacenterLabel}
                  onSelect={handleDataCenterSelect}
                  minWidthClass="min-w-[200px]"
                  maxWidthClass="max-w-[280px]"
                  fixedPosition={true}
                  dropdownMaxHeightClass="max-h-48"
                />
              </div>
              <div className="flex flex-col">
                <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                  Select Hardware
                </label>
                <GenericDropdown
                  items={hardwareOptions}
                  defaultLabel="Select hardware"
                  value={isLoadingHardware ? "Loading..." : selectedHardwareLabel}
                  onSelect={handleHardwareSelect}
                  disabled={!isHardwareEnabled || isLoadingHardware}
                  minWidthClass="min-w-[200px]"
                  maxWidthClass="max-w-[240px]"
                  fixedPosition={true}
                  dropdownMaxHeightClass="max-h-48"
                />
              </div>
            </div>
            <GenericButton
              label={
                <div className="flex items-center gap-2">
                  <IconPlus className="w-5 h-5" />
                  <span>Add new port</span>
                </div>
              }
              variant="brand"
              size="md"
              onClick={handleCreatePort}
            />
          </div>
        </GenericCard>

        <GenericCard isCollapsible={false}>
          <div className="p-5">
            <div className="flex items-center gap-4 my-5">
              <div className="w-[520px]">
                <GenericSearchbar
                  placeholder="Find by attribute or value"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={handleFiltersChange}
                  liveFiltering={false}
                />
              </div>
            </div>

            {/* Table */}
            {isFetching ? (
              <TableSkeleton columns={5} rows={5} />
            ) : (
              <GenericTable
                columns={columns}
                data={filteredPorts}
                initialRowsPerPage={20}
                rowsPerPageOptions={[10, 20, 30]}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                width="100%"
                verticalAlign="center"
                maxTableBodyHeight={
                  // Calculate height based on rowsPerPage (visible rows) + header
                  // ~45px per row + ~60px for header and spacing
                  `${Math.min(Math.max(400, rowsPerPage * 45 + 60), 800)}px`
                }
              />
            )}
          </div>
        </GenericCard>
      </div>

      {/* View Port Modal */}
      <PortViewModal
        isOpen={isViewModalOpen}
        isLoading={isViewLoading}
        port={selectedPort}
        onClose={() => setIsViewModalOpen(false)}
      />

      {/* Edit Port Modal */}
      <PortEditModal
        isOpen={isEditModalOpen}
        isLoading={isEditLoading}
        port={selectedPort}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSavePort}
      />

      {/* Create Port Modal */}
      <PortCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleSavePort}
      />

      {/* Delete Confirmation */}
      <GenericAlert
        isOpen={isAlertOpen}
        onClose={() => {
          setIsAlertOpen(false);
          setPortToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        description="This action cannot be undone. This will permanently delete the port."
        cancelLabel="Cancel"
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
      />
    </>
  );
}
